// MapPickerModal — search a location, drop a pin, confirm lat/lng
// Uses Leaflet (free) + Nominatim/OpenStreetMap geocoding (no API key needed)

import { useEffect, useRef, useState } from 'react';
import Button from '../ui/Button';

export default function MapPickerModal({ onConfirm, onClose, initialLat, initialLng }) {
  const mapRef      = useRef(null);
  const leafletMap  = useRef(null);
  const markerRef   = useRef(null);

  const [search,  setSearch]  = useState('');
  const [results, setResults] = useState([]);
  const [coords,  setCoords]  = useState({
    lat: initialLat || 28.6139,
    lng: initialLng || 77.2090,
  });
  const [searching, setSearching] = useState(false);
  const [locating,  setLocating]  = useState(false);

  // Skips the next "recenter to zoom 16" reaction — used when we move the
  // view ourselves (e.g. initial wide geolocation view) and don't want the
  // precision zoom meant for search/click/drag picks.
  const skipNextRecenter = useRef(false);

  // Asks the browser for the device's current position (the admin's laptop).
  // Silent on failure/denial — falls back to the default view, no error UI
  // since this is a convenience, not a requirement.
  function locateMe(onSuccess) {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        onSuccess(pos.coords.latitude, pos.coords.longitude);
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  }

  // Inject Leaflet CSS once
  useEffect(() => {
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id   = 'leaflet-css';
      link.rel  = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
  }, []);

  // Init Leaflet map after modal mounts
  useEffect(() => {
    if (!mapRef.current) return;
    let cancelled = false;

    import('leaflet').then(L => {
      if (cancelled || leafletMap.current) return; // already initialised, or unmounted before this resolved

      const map = L.map(mapRef.current).setView([coords.lat, coords.lng], 14);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
      }).addTo(map);

      // Custom draggable pin
      const icon = L.divIcon({
        className: '',
        html: '<div style="font-size:32px;line-height:1;margin-top:-28px;margin-left:-12px">📍</div>',
        iconSize: [24, 32],
      });

      const marker = L.marker([coords.lat, coords.lng], { draggable: true, icon }).addTo(map);
      marker.on('dragend', () => {
        const { lat, lng } = marker.getLatLng();
        setCoords({ lat: +lat.toFixed(6), lng: +lng.toFixed(6) });
      });

      map.on('click', (e) => {
        const { lat, lng } = e.latlng;
        marker.setLatLng([lat, lng]);
        setCoords({ lat: +lat.toFixed(6), lng: +lng.toFixed(6) });
      });

      leafletMap.current = map;
      markerRef.current  = marker;

      // No saved location yet (new guardian/student) — default the view to
      // the admin's current location instead of a hardcoded city, zoomed
      // out enough to cover the ~25-35km radius students can be spread
      // across, so they don't have to search/pan from Delhi every time.
      if (!initialLat) {
        locateMe((lat, lng) => {
          skipNextRecenter.current = true;
          map.setView([lat, lng], 10);
          marker.setLatLng([lat, lng]);
          setCoords({ lat: +lat.toFixed(6), lng: +lng.toFixed(6) });
        });
      }
    });

    return () => {
      cancelled = true;
      if (leafletMap.current) {
        leafletMap.current.remove();
        leafletMap.current = null;
        markerRef.current  = null;
      }
    };
  }, []); // eslint-disable-line

  // Move map + marker when coords change (from search result)
  useEffect(() => {
    if (!leafletMap.current || !markerRef.current) return;
    if (skipNextRecenter.current) { skipNextRecenter.current = false; return; }
    leafletMap.current.setView([coords.lat, coords.lng], 16);
    markerRef.current.setLatLng([coords.lat, coords.lng]);
  }, [coords]);

  async function handleSearch(e) {
    e.preventDefault();
    if (!search.trim()) return;
    setSearching(true);
    setResults([]);
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(search)}&limit=5`;
      const res = await fetch(url, { headers: { 'Accept-Language': 'en' } });
      const data = await res.json();
      setResults(data);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }

  function pickResult(r) {
    setCoords({ lat: +parseFloat(r.lat).toFixed(6), lng: +parseFloat(r.lon).toFixed(6) });
    setResults([]);
    setSearch(r.display_name.split(',').slice(0, 2).join(','));
  }

  const inputCls = "border border-line rounded-s px-3 py-2 text-sm bg-paper-raised focus:outline-none focus:ring-2 focus:ring-transit";

  return (
    <div className="fixed inset-0 bg-ink/40 flex items-center justify-center z-[1000]" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-paper-raised rounded-l shadow-2 w-[620px] max-w-[95vw] max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center px-4 py-3.5 border-b border-line-soft">
          <span className="font-display font-bold text-base text-ink">📍 Pick Location</span>
          <button onClick={onClose} className="bg-transparent border-none text-lg cursor-pointer text-ink-soft hover:text-ink">✕</button>
        </div>

        {/* Search bar */}
        <form onSubmit={handleSearch} className="flex gap-2 px-4 py-2.5">
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search address, landmark, metro station…"
            className={`${inputCls} flex-1`}
            autoFocus
          />
          <Button type="submit" disabled={searching} className="!px-3.5">
            {searching ? '…' : '🔍'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            title="Center on my current location"
            disabled={locating}
            className="!px-3.5"
            onClick={() => locateMe((lat, lng) =>
              setCoords({ lat: +lat.toFixed(6), lng: +lng.toFixed(6) }))}
          >
            {locating ? '…' : '📍'}
          </Button>
        </form>

        {/* Autocomplete results */}
        {results.length > 0 && (
          <ul className="mx-4 mb-2 p-0 list-none border border-line rounded-s max-h-40 overflow-y-auto">
            {results.map((r, i) => (
              <li key={i} onClick={() => pickResult(r)}
                className="px-3 py-2 text-[13px] cursor-pointer border-b border-line-soft last:border-b-0 hover:bg-transit-tint/40">
                {r.display_name}
              </li>
            ))}
          </ul>
        )}

        {/* Leaflet map */}
        <div className="text-ink-soft text-xs pl-4 pb-1">
          Map opens near your current location — click anywhere on it, drag the pin to
          fine-tune, or search/tap 📍 to re-center
        </div>
        <div ref={mapRef} style={{ flex: 1, minHeight: 320, margin: '0 0 0 0' }} />

        {/* Coordinates readout + confirm */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-line-soft gap-3">
          <span className="text-[13px] text-ink-soft">
            Lat: <strong className="text-ink">{coords.lat}</strong> &nbsp; Lng: <strong className="text-ink">{coords.lng}</strong>
          </span>
          <Button onClick={() => onConfirm(coords.lat, coords.lng)}>
            ✅ Confirm Location
          </Button>
        </div>
      </div>
    </div>
  );
}
